import React, { useMemo, useState } from "react";
import styled from "styled-components";
import {
  buildRolePermissionSections,
  canEditPermissions,
  FEATURE_KEYS,
  FEATURE_LABELS,
  getFeatureAccessMatrix,
  ROLE_LABELS,
  ROLE_ORDER,
  saveFeatureAccessMatrix,
  resetFeatureAccessMatrix,
} from "../utils/rbac";
import { getStoredUser } from "../utils/impersonation";

const RolePermissionsPage = () => {
  const user = useMemo(() => getStoredUser(), []);
  const isEditable = canEditPermissions(user);
  const [matrix, setMatrix] = useState(() => getFeatureAccessMatrix());
  const [note, setNote] = useState("");

  const sections = useMemo(() => buildRolePermissionSections(matrix), [matrix]);

  const toggleRoleAccess = (feature, role) => {
    if (feature === "impersonation") return;
    if (["owner", "admin", "super_admin", "master"].includes(role)) return;
    setMatrix((prev) => {
      const current = new Set(prev?.[feature] || []);
      if (current.has(role)) current.delete(role);
      else current.add(role);
      return { ...prev, [feature]: Array.from(current) };
    });
  };

  const onSave = () => {
    saveFeatureAccessMatrix(matrix);
    setNote("Permissions updated successfully.");
  };

  const onReset = () => {
    resetFeatureAccessMatrix();
    setMatrix(getFeatureAccessMatrix());
    setNote("Permissions reset to default.");
  };

  return (
    <Page>
      <Hero>
        <h1>Role Permissions</h1>
        <p>
          {isEditable
            ? "Change role permissions using the matrix below and save."
            : "View-only mode. You do not have permission to edit role access."}
        </p>
      </Hero>

      <EditorCard>
        <HeadRow>
          <h3>Permission Matrix</h3>
          <ActionRow>
            <ActionBtn type="button" onClick={onReset} disabled={!isEditable}>
              Reset
            </ActionBtn>
            <ActionBtn type="button" $primary onClick={onSave} disabled={!isEditable}>
              Save Changes
            </ActionBtn>
          </ActionRow>
        </HeadRow>
        {note && <Hint>{note}</Hint>}
        <TableWrap>
          <MatrixTable>
            <thead>
              <tr>
                <th>Feature</th>
                {ROLE_ORDER.map((role) => (
                  <th key={role}>{ROLE_LABELS[role] || role}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {FEATURE_KEYS.map((feature) => (
                <tr key={feature}>
                  <td>{FEATURE_LABELS[feature] || feature}</td>
                  {ROLE_ORDER.map((role) => {
                    const checked = (matrix?.[feature] || []).includes(role);
                    const locked =
                      !isEditable ||
                      feature === "impersonation" ||
                      ["owner", "admin", "super_admin", "master"].includes(role);
                    return (
                      <td key={`${feature}-${role}`}>
                        <input
                          type="checkbox"
                          checked={checked}
                          disabled={locked}
                          onChange={() => toggleRoleAccess(feature, role)}
                        />
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </MatrixTable>
        </TableWrap>
      </EditorCard>

      <Grid>
        {sections.map((section) => (
          <Card key={section.title}>
            <Head>
              <Badge style={{ background: `${section.color}20`, color: section.color }}>
                {section.title}
              </Badge>
            </Head>
            <List>
              {section.features.map((feature) => (
                <li key={feature}>{FEATURE_LABELS[feature] || feature}</li>
              ))}
            </List>
          </Card>
        ))}
      </Grid>
    </Page>
  );
};

export default RolePermissionsPage;

const Page = styled.div`
  min-height: 100vh;
  padding: 24px;
  background: #f4f7fb;
  display: grid;
  align-content:start;gap: 16px;
  @media(max-width:650px){padding:14px;}
`;

const Hero = styled.div`
  background:linear-gradient(135deg,#075f9e,#0787c5);color:#fff;
  border-radius:22px;padding:27px 30px;box-shadow:0 14px 30px rgba(1,91,154,.17);
  h1 { margin: 0; font-size: 2rem; }
  p { margin: 6px 0 0; color: #dff3ff; }
  @media(max-width:600px){padding:22px 18px;}
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 14px;
`;

const EditorCard = styled.div`
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 18px;padding:18px;box-shadow:0 6px 18px rgba(15,23,42,.04);
`;

const HeadRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 10px;
  align-items: center;
  flex-wrap: wrap;
  h3 {
    margin: 0;
    color: #0f172a;
  }
`;

const ActionRow = styled.div`
  display: inline-flex;
  gap: 8px;
`;

const ActionBtn = styled.button`
  border-radius: 10px;
  border: 1px solid ${({ $primary }) => ($primary ? "#0874af" : "#cbd5e1")};
  background: ${({ $primary }) => ($primary ? "#0874af" : "#fff")};
  color: ${({ $primary }) => ($primary ? "#fff" : "#0f172a")};
  font-weight: 700;
  padding: 8px 12px;
  cursor: pointer;
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Hint = styled.div`
  margin-top: 8px;
  font-size: 12px;
  color: #166534;
`;

const TableWrap = styled.div`
  margin-top: 10px;
  overflow-x: auto;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
`;

const MatrixTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 860px;
  th,
  td {
    border-bottom: 1px solid #e2e8f0;
    padding: 8px 10px;
    text-align: center;
    font-size: 12px;
  }
  th:first-child,
  td:first-child {
    text-align: left;
    font-weight: 700;
    color: #0f172a;
    min-width: 180px;
  }
  thead {
    background: #eaf6fc;color:#315166;
  }
`;

const Card = styled.div`
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 16px;padding:16px;box-shadow:0 5px 16px rgba(15,23,42,.04);
`;

const Head = styled.div`
  margin-bottom: 10px;
`;

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 6px 10px;
  font-size: 12px;
  font-weight: 800;
`;

const List = styled.ul`
  margin: 0;
  padding-left: 18px;
  color: #334155;
  display: grid;
  gap: 6px;
`;
